import { useAppContext, UserSchema } from 'src/appState';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { PageTitle } from 'elements/Typography';
import { useFormikFromForZod } from 'src/form-utils';
import { GenerateErrorSchema, GenerateSchema, useQerkoMutation } from 'src/apiClient';
import { InputTextBoxWithLabel } from 'elements/form/InputTextWithLabel';
import { MyButton } from 'elements/Buttons';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { CheckboxWithLabel } from 'elements/form/CheckboxWithLabel';
import { SelectWithLabel } from 'elements/form/SelectWithLabel';
import { Language, PhoneSchema } from 'src/shared/types';
import { omit } from 'lodash';

const LoginResponseUserSchema = UserSchema.extend({ accessToken: z.string() });

const LoginResponseSchema = z.union([
    GenerateSchema(LoginResponseUserSchema, 200),
    GenerateErrorSchema(401),
]);

const Registration = () => {
    const { setAuth } = useAppContext();
    const { t, i18n } = useTranslation('restaurantAdmin.registration');
    const RegisterSchema = useMemo(() => z.object({
        email: z.string().email(t('error.message.email')),
        language: z.nativeEnum(Language),
        name: z.string().nonempty(t('error.message.name')),
        password: z.string().nonempty(t('error.message.password')),
        phone: PhoneSchema,
        surname: z.string().nonempty(t('error.message.surname')),
    }), [t]);
    const RegisterFormSchema = useMemo(() => RegisterSchema.extend({
        licenceAgree: z.boolean().refine((val: boolean) => val, { message: t('error.message.agree') }),
        password2: z.string().optional(),
    }).refine((values) => {
        return !z.string().safeParse(values.password2).success || values.password2 === values.password;
    }, { message: t('error.message.reEnterPassword'), path: ['password2'] }), [RegisterSchema, t]);
    const { mutateAsync: register } = useQerkoMutation(
        'POST',
        () => '/api/v1/restaurant-admin/user',
        RegisterSchema,
        z.union([GenerateSchema(z.unknown(), 201), GenerateErrorSchema(400), GenerateErrorSchema(409)]),
    );
    const { mutateAsync: login } = useQerkoMutation('POST', () => '/api/v1/restaurant-admin/login', z.object({ password: z.string(), username: z.string() }), LoginResponseSchema);
    const { enqueueSnackbar } = useSnackbar();
    const formik = useFormikFromForZod(
        RegisterFormSchema,
        {
            initialValues: {
                email: '',
                language: z.nativeEnum(Language).safeParse(i18n.language).success ? z.nativeEnum(Language).parse(i18n.language) : Language.en,
                licenceAgree: false,
                name: '',
                password: '',
                password2: '',
                phone: '',
                surname: '',
            },
            onSubmit: async (values) => {
                const response = await register(omit(values, ['licenceAgree', 'password2']));
                if (response === undefined) {
                    return;
                }
                if (response.statusCode === 201) {
                    const loginResponse = await login({ password: values.password, username: values.email });
                    if (loginResponse === undefined) {
                        return;
                    }

                    if (loginResponse.statusCode === 401) {
                        enqueueSnackbar(t('error.message.userRegistered'), { variant: 'error' });
                        return;
                    }

                    setAuth({
                        accessToken: loginResponse.body.accessToken,
                        restaurants: [],
                        user: loginResponse.body,
                    }, null);
                    enqueueSnackbar(`Welcome ${loginResponse.body.email}`, { variant: 'success' });
                    return;
                } if (response.statusCode === 409) {
                    enqueueSnackbar(t('error.message.userAlreadyExists'), { variant: 'error' });
                    return;
                }
                enqueueSnackbar(response?.body?.message || t('error.message.complications'), { variant: 'error' });

            },
        });
    const router = useRouter();
    return (
        <Container maxWidth="sm" sx={{ pt: '2rem' }}>
            <Box alignItems="center" display="flex" flexDirection="column">
                <Box sx={{ pb: '2rem' }}>
                    <img alt="Qerko Logo" src={`${router.basePath}/qerko-logo.svg`} width={180} />
                </Box>
                <PageTitle>{t('page.title.userRegistration')}</PageTitle>
            </Box>
            <form onSubmit={formik.handleSubmit}>
                <InputTextBoxWithLabel
                    label={t('input.label.name')}
                    {...formik.stringInputProps('name')}
                />
                <InputTextBoxWithLabel
                    label={t('input.label.surname')}
                    {...formik.stringInputProps('surname')}
                />
                <InputTextBoxWithLabel
                    label={t('input.label.email')}
                    {...formik.stringInputProps('email')}
                />
                <InputTextBoxWithLabel
                    label={t('input.label.phone')}
                    placeholder="+420602333444"
                    {...formik.stringInputProps('phone')}
                />
                <SelectWithLabel
                    label={t('input.label.preferredLanguage')}
                    nonEmpty
                    options={Object.values(Language).map((lang) => ({ label: lang, value: lang }))}
                    {...formik.stringInputProps('language')}
                    fullWidth
                />
                <InputTextBoxWithLabel
                    label={t('input.label.password')}
                    type="password"
                    {...formik.stringInputProps('password')}
                />
                <InputTextBoxWithLabel
                    label={t('input.label.reEnterPassword')}
                    type="password"
                    {...formik.stringInputProps('password2')}
                />
                <CheckboxWithLabel bottomText={formik.errors.licenceAgree ? <Typography color="error">{formik.errors.licenceAgree}</Typography> : ''} label={<>{t('input.label.agree')} <a href="/terms/general-terms">{t('input.label.terms')}</a></>} {...formik.checkboxProps('licenceAgree')} />
                <Box display="flex" justifyContent="center"><MyButton loading={formik.isSubmitting} onClick={() => formik.submitForm()} showLoading>{t('button.title.submit')}</MyButton></Box>
            </form>
        </Container>
    );
};

export default Registration;
